import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Users, CheckCircle2, ChevronRight, GraduationCap, Stethoscope, HeartPulse, Brain, Heart, ArrowRight } from 'lucide-react';
import ProgrammePageShell from './ProgrammePageShell';
import { Link } from 'react-router-dom';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const TeamCard = ({ icon: Icon, title, role, desc }) => (
  <motion.div
    variants={fadeUp}
    className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg p-6 transition-all duration-300 hover:-translate-y-1 group"
  >
    <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#800000] transition-colors duration-300">
      <Icon className="w-6 h-6 text-[#800000] group-hover:text-white transition-colors duration-300" />
    </div>
    <h3 className="font-bold text-slate-900 text-lg mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
    <p className="text-[#800000] font-medium text-sm mb-3">{role}</p>
    <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
  </motion.div>
);

const FAQ = ({ q, a }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors"
        aria-expanded={open}
      >
        <span>{q}</span>
        <ChevronRight className={`w-5 h-5 text-[#800000] flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="px-6 pb-4 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-3">{a}</div>}
    </div>
  );
};

const ExpertMedicalTeam = () => {
  const teamRoles = [
    { icon: Stethoscope, title: 'Bariatric & Metabolic Surgeons', role: 'Surgical Excellence', desc: 'World-class surgeons specialising in minimally invasive techniques, ensuring the highest safety standards and optimal long-term outcomes.' },
    { icon: HeartPulse, title: 'Endocrinologists', role: 'Hormonal & Metabolic Care (On Call Visiting)', desc: 'On Call Visiting Endocrinologists who manage the complex hormonal imbalances often driving obesity, including thyroid issues, insulin resistance, and PCOS.' },
    { icon: GraduationCap, title: 'Clinical Dietitians', role: 'Nutritional Strategy', desc: 'Specialised nutritionists who design practical, sustainable meal plans that fit your lifestyle while ensuring optimal nutritional intake.' },
    { icon: Brain, title: 'Psychologists & Counsellors', role: 'Behavioural Health (On Call Visiting)', desc: 'On Call Visiting Psychologists & Counsellors helping you overcome emotional eating, build healthy habits, and navigate the psychological aspects of weight loss.' },
    { icon: Heart, title: 'Cardiologists & Pulmonologists', role: 'Comorbidity Management (On Call Visiting)', desc: 'On Call Visiting Cardiologists & Pulmonologists ensuring your heart and lungs are healthy, managing hypertension, sleep apnoea, and preparing you safely for any procedures.' },
    { icon: Users, title: 'Dedicated Care Coordinators', role: 'Patient Navigation', desc: 'Your personal guides through the entire programme, managing appointments, answering questions, and ensuring a seamless experience.' },
  ];

  const faqs = [
    { q: 'Will I see the same doctor at every visit?', a: 'You will have a primary lead physician who oversees your entire care journey. However, because we use a multi-disciplinary approach, you will also have dedicated appointments with our dietitians, psychologists, and other specialists as dictated by your personalised care plan.' },
    { q: 'Are your surgeons experienced in complex cases?', a: 'Yes. Our surgical team has performed thousands of successful bariatric and metabolic procedures, including complex revisions. We are a recognised centre of excellence for advanced minimally invasive surgery.' },
    { q: 'Do I need a referral to see the specialist team?', a: 'No, you do not need a referral to begin our programme. You can book an initial consultation directly with us, and we will coordinate all necessary specialist assessments internally.' },
    { q: 'How does the team collaborate on my care?', a: 'We hold regular multi-disciplinary team (MDT) meetings where your specialists—surgeons, gastroenterologists, dietitians, and psychologists—convene to review your progress and collaboratively adjust your treatment plan.' },
    { q: 'What if I only need dietary advice, not surgery?', a: 'Our team is equipped to provide whatever level of intervention is clinically appropriate and aligned with your goals. Many of our patients succeed purely through our medical weight management and nutritional counselling tracks without surgery.' },
  ];

  return (
    <>
      <Helmet>
        <title>Expert Medical Team | GastroCare Bhopal</title>
        <meta name="description" content="Meet the expert multi-disciplinary medical team at GastroCare Bhopal. Our bariatric surgeons, endocrinologists, dietitians, and psychologists work together for your success." />
        <meta property="og:title" content="Expert Medical Team | GastroCare Bhopal" />
        <meta property="og:description" content="A collaborative approach to obesity treatment involving world-class surgeons, endocrinologists, and clinical dietitians." />
      </Helmet>

      <ProgrammePageShell
        title="Expert Medical Team"
        subtitle="Obesity is a complex condition that no single doctor can treat alone. At GastroCare Bhopal, you are supported by a coordinated, multi-disciplinary board of specialists dedicated to your transformation."
        icon={Users}
        breadcrumb="Expert Medical Team"
      >
        {/* Introduction */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp}>
              <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">The MDT Approach</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                A Board of Specialists, Focused on You
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Successful, long-term weight management requires more than just a diet plan or a surgical procedure. It requires a fundamental shift in metabolism, behaviour, and lifestyle, supported by rigorous medical science.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                That is why we employ a Multi-Disciplinary Team (MDT) approach. When you join our programme, you don't just get a doctor; you gain an entire board of experts. Our surgeons, endocrinologists, dietitians, and psychologists collaborate constantly to ensure every aspect of your health is optimised.
              </p>
              <p className="text-slate-600 leading-relaxed">
                This collaborative model ensures higher safety standards, better clinical outcomes, and a comprehensive support system that addresses the root causes of obesity, not just the symptoms.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} className="relative">
              <img
                src={process.env.PUBLIC_URL + "/img-2.png"} // Reusing the high-quality image from the main page
                alt="Expert medical team collaborating"
                className="rounded-2xl w-full h-72 lg:h-96 object-cover shadow-xl"
                loading="lazy"
              />
              <div className="absolute -top-4 -left-4 bg-white rounded-2xl p-4 shadow-lg hidden md:flex items-center gap-4">
                <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-[#800000]" />
                </div>
                <div>
                  <p className="text-xl font-extrabold text-slate-900 leading-none">MDT</p>
                  <p className="text-xs text-slate-500 font-medium">Certified Approach</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Team Roles */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-24">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">Our Experts</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>The Specialists Supporting You</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            {teamRoles.map((role, i) => <TeamCard key={i} {...role} />)}
          </div>
        </motion.section>

        {/* Why Choose Us Highlight */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-24 bg-[#800000] rounded-3xl p-8 lg:p-12 text-white overflow-hidden relative">
          <div aria-hidden className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div variants={fadeUp}>
              <h2 className="text-3xl sm:text-4xl font-extrabold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Meet Dr. Sanjay Kumar</h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Leading our multi-disciplinary team is Dr. Sanjay Kumar. With decades of experience and thousands of transformed lives, his patient-first philosophy forms the core of GastroCare's approach.
              </p>
              {/* <Link to="/expert" className="inline-flex items-center gap-2 bg-white text-[#800000] hover:bg-slate-100 font-bold px-6 py-3 rounded-full transition-all duration-300">
                  View Full Profile
                  <ArrowRight className="w-4 h-4" />
                </Link> */}
            </motion.div>
            <motion.div variants={fadeUp} className="hidden lg:block">
              {/* Decorative elements representing expertise */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl text-center">
                  <p className="text-4xl font-extrabold mb-2">20+</p>
                  <p className="text-sm font-medium opacity-90">Years Experience</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl text-center">
                  <p className="text-4xl font-extrabold mb-2">5000+</p>
                  <p className="text-sm font-medium opacity-90">Successful Surgeries</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>


        {/* FAQs */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Frequently Asked Questions</h2>
          </motion.div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => <FAQ key={i} q={f.q} a={f.a} />)}
          </div>
        </motion.section>
      </ProgrammePageShell>
    </>
  );
};

export default ExpertMedicalTeam;
